import type { PublishedBusinessData } from "../../../types/business";

export function defaultHeroDescription(
  business: Pick<PublishedBusinessData, "serviceArea">,
): string {
  return `Serving ${business.serviceArea} with reliable craftsmanship, clear communication, and work done right the first time.`;
}

export type HeroHeadlineParts =
  | { kind: "default"; tradeDisplayName: string }
  | { kind: "custom_one"; line: string }
  | { kind: "custom_two"; firstLine: string; accentLine: string };

/** Splits a single-line headline at a word boundary: first half of words, then the rest (accent line). */
export function splitHeroHeadlineWords(headline: string): {
  firstLine: string;
  accentLine: string;
} | null {
  const words = headline.trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) {
    return null;
  }
  const nFirst = Math.floor(words.length / 2);
  const firstLine = words.slice(0, nFirst).join(" ");
  const accentLine = words.slice(nFirst).join(" ");
  if (!firstLine || !accentLine) {
    return null;
  }
  return { firstLine, accentLine };
}

export function resolveHeroHeadlineParts(
  business: Pick<PublishedBusinessData, "tradeDisplayName" | "heroHeadline">,
): HeroHeadlineParts {
  const src = business.heroHeadline ?? "";
  if (!src.trim()) {
    return { kind: "default", tradeDisplayName: business.tradeDisplayName };
  }

  const nl = src.search(/\r?\n/);
  if (nl !== -1) {
    const first = src.slice(0, nl).trim();
    const second = src.slice(nl + 1).trim();
    if (second) {
      return { kind: "custom_two", firstLine: first, accentLine: second };
    }
    return { kind: "custom_one", line: first || src.trim() };
  }

  const raw = src.trim();
  const auto = splitHeroHeadlineWords(raw);
  if (auto) {
    return {
      kind: "custom_two",
      firstLine: auto.firstLine,
      accentLine: auto.accentLine,
    };
  }
  return { kind: "custom_one", line: raw };
}
